import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { QuickAnalysisResult } from '../../../entities/analysis';
import { AnalysisIssue } from '../../../entities/issue';

interface LocalProjectData {
    results: (QuickAnalysisResult & { fileName: string })[];
    aggregatedIssues: AnalysisIssue[];
    lastUpdated: string;
}

interface AnalysisState {
    projectData: Record<string, LocalProjectData>;
    activeJobId: string | null;
    setActiveJobId: (jobId: string | null) => void;
    addResult: (projectId: string, fileName: string, result: QuickAnalysisResult) => void;
    clearProjectData: (projectId: string) => void;
    resetStore: () => void;
}

export const useAnalysisStore = create<AnalysisState>()(
    persist(
        (set) => ({
            projectData: {},
            activeJobId: null,

            setActiveJobId: (jobId) => set({ activeJobId: jobId }),

            addResult: (projectId, fileName, result) => {
                set((state) => {
                    const current = state.projectData[projectId] || {
                        results: [],
                        aggregatedIssues: [],
                        lastUpdated: new Date().toISOString()
                    };

                    const newResult = { ...result, fileName };

                    // We only keep the last 50 results per project to avoid storage bloat
                    const updatedResults = [newResult, ...current.results].slice(0, 50);

                    // Re-aggregate issues from all current results
                    const allIssues = updatedResults.flatMap(r => r.issues);

                    return {
                        projectData: {
                            ...state.projectData,
                            [projectId]: {
                                results: updatedResults,
                                aggregatedIssues: allIssues,
                                lastUpdated: new Date().toISOString()
                            }
                        }
                    };
                });
            },

            clearProjectData: (projectId) => {
                set((state) => {
                    const newData = { ...state.projectData };
                    delete newData[projectId];
                    return { projectData: newData };
                });
            },

            resetStore: () => {
                set({ projectData: {}, activeJobId: null });
            }
        }),
        {
            name: 'devlint-analysis-storage'
        }
    )
);
